import React, { useCallback, useEffect, useState } from 'react'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import FileSaver from 'file-saver'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import Toolbar from '@mui/material/Toolbar'
import Slide from '@mui/material/Slide'
import Tooltip from '@mui/material/Tooltip'
import { TransitionProps } from '@mui/material/transitions'

import CloseIcon from '@mui/icons-material/Close'
import DownloadIcon from '@mui/icons-material/Download'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import UndoRoundedIcon from '@mui/icons-material/UndoRounded'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import TextSnippetIcon from '@mui/icons-material/TextSnippet'
import CircularProgress from '@mui/material/CircularProgress'

import FilePreview, { FilePreviewProps } from '@/components/ui/preview/FilePreview'
import TextInput from '@/components/ui/inputs/TextInput'
import { supportsOcr } from '@/utils/ocr'

export type FilePreviewDialogProps = {
  disableChangeFileName?: boolean
  /**
   * file name
   */
  fileName: string
  /**
   * file type
   */
  fileType: string
  /**
   * file key (S3 key) for OCR operations
   */
  fileKey?: string
  /**
   * Callback fired when the component requests to be closed.
   */
  onClose?: () => void
  /**
   * Callback fired when save new file name
   */
  onSaveFileName?: (filename: string) => void
  /**
   * Callback fired when next button is clicked
   */
  onNext?: () => void
  /**
   * Callback fired when previous button is clicked
   */
  onPrevious?: () => void
  /**
   * Callback fired when OCR button is clicked
   */
  onOcrDocument?: (fileKey: string) => void
  /**
   * Whether OCR is currently processing
   */
  isProcessingOcr?: boolean
  /**
   * Key of the file currently being processed by OCR
   */
  processingOcrFileKey?: string | null
  /**
   * If `true`, the component is shown
   */
  open: boolean
  PhotoViewerProps?: FilePreviewProps['PhotoViewerProps']
  /**
   * file source
   */
  src: string
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />
})

export function FilePreviewDialog(props: FilePreviewDialogProps) {
  const {
    disableChangeFileName = false,
    fileName,
    fileType,
    fileKey,
    onClose,
    onSaveFileName,
    onNext,
    onPrevious,
    onOcrDocument,
    isProcessingOcr = false,
    processingOcrFileKey = null,
    open,
    PhotoViewerProps,
    src,
  } = props
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const [downloading, setDownloading] = useState<boolean>(false)
  const [hasError, setHasError] = useState<boolean>(false)
  const [newFileName, setNewFileName] = useState<string>(fileName)

  const isOcrSupported = supportsOcr(fileType)

  // Update newFileName when fileName prop changes (when switching files)
  useEffect(() => {
    setNewFileName(fileName)
  }, [fileName])

  const handleChangeNewFileName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewFileName(event.target.value)
  }

  const handleClose = () => {
    if (onClose) {
      onClose()
      setNewFileName(fileName)
    }
  }

  const handleDownload = async () => {
    setDownloading(true)
    try {
      console.log({ fileName, fileType, src })
      const messageData = JSON.stringify({ fileName, fileType, src })
      if (window.ReactNativeWebView) window.ReactNativeWebView.postMessage(messageData)
    } catch (err) {
      console.log('Error posting message:', err)
    }
    try {
      // Handle blob URLs (offline documents) differently
      if (src.startsWith('blob:')) {
        const response = await fetch(src)
        const blob = await response.blob()
        FileSaver.saveAs(blob, `${fileName}.${fileType}`)
      } else {
        const response = await fetch(src)
        if (response.ok && response.status === 200) {
          const blob = await response.blob()
          FileSaver.saveAs(blob, `${fileName}.${fileType}`)
        } else {
          enqueueSnackbar(t('error.file.failedToLoad.message'), { variant: 'error' })
        }
      }
    } catch (err) {
      console.log(err)
      enqueueSnackbar(t('error.file.failedToLoad.message'), { variant: 'error' })
    } finally {
      setDownloading(false)
    }
  }

  const handleLoadFileError = useCallback(() => {
    setHasError(true)
  }, [])

  const handleSaveNewFileName = () => {
    onSaveFileName?.(`${newFileName}.${fileType}`)
  }

  const handleUndoFileName = () => {
    setNewFileName(fileName)
  }

  return (
    <Dialog fullScreen open={open} onClose={handleClose} TransitionComponent={Transition}>
      {downloading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            width: '100%',
            zIndex: (theme) => theme.zIndex.appBar + 1,
            color: (theme) => theme.palette.common.white,
          }}
        >
          <LinearProgress color="inherit" />
        </Box>
      )}
      <AppBar position="fixed">
        <Toolbar>
          <Stack direction="row" spacing={2} sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
              <TextInput
                disabled={disableChangeFileName}
                fullWidth
                onChange={handleChangeNewFileName}
                value={newFileName}
                InputProps={{
                  sx: {
                    '& .MuiOutlinedInput-input.Mui-disabled': {
                      cursor: 'unset',
                      WebkitTextFillColor: 'unset',
                    },
                    '&:not(.Mui-focused)': {
                      background: 'none',
                      boxShadow: 'none',
                      color: 'background.paper',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  },
                }}
              />
              {newFileName !== fileName && !/[~!@#$%^&*;:,.<>\\/]/.test(newFileName) && (
                <Tooltip title={t('common:button.save.tooltip')}>
                  <IconButton color="inherit" onClick={handleSaveNewFileName}>
                    <SaveRoundedIcon />
                  </IconButton>
                </Tooltip>
              )}
              {newFileName !== fileName && (
                <Tooltip title={t('common:button.undo.tooltip')}>
                  <IconButton color="inherit" onClick={handleUndoFileName}>
                    <UndoRoundedIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              {onPrevious && (
                <Tooltip title={t('common:button.previous.tooltip')}>
                  <IconButton color="inherit" onClick={onPrevious}>
                    <ArrowBackIosNewIcon />
                  </IconButton>
                </Tooltip>
              )}
              {onNext && (
                <Tooltip title={t('common:button.next.tooltip')}>
                  <IconButton color="inherit" onClick={onNext}>
                    <ArrowForwardIosIcon />
                  </IconButton>
                </Tooltip>
              )}
              {isOcrSupported && fileKey && (onOcrDocument || (processingOcrFileKey === fileKey && isProcessingOcr)) && (
                <Tooltip title={t('common:button.ocr.tooltip', { defaultValue: 'Extract biological tests and vital signs from this document' })}>
                  <span>
                    <IconButton color="inherit" disabled={hasError || isProcessingOcr || !onOcrDocument} onClick={() => onOcrDocument?.(fileKey)}>
                      {processingOcrFileKey === fileKey && isProcessingOcr ? <CircularProgress size={24} color="warning" /> : <TextSnippetIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title={t('common:button.download.tooltip')}>
                <span>
                  <IconButton color="inherit" disabled={hasError} onClick={handleDownload}>
                    <DownloadIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('common:button.openInNewTab.tooltip')}>
                <span>
                  <IconButton
                    component="a"
                    color="inherit"
                    disabled={hasError || src.startsWith('blob:')}
                    href={src.startsWith('blob:') ? undefined : src}
                    target="_blank"
                    onClick={src.startsWith('blob:') ? (e) => e.preventDefault() : undefined}
                  >
                    <OpenInNewIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('common:button.close.tooltip')}>
                <IconButton color="inherit" onClick={handleClose}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Toolbar>
      </AppBar>
      <Toolbar />
      <Box sx={{ display: 'flex', pt: '10px', pb: '10px', justifyContent: 'center' }}>
        <FilePreview fileName={fileName} fileType={fileType} filePath={src} onError={handleLoadFileError} PhotoViewerProps={PhotoViewerProps} />
      </Box>
    </Dialog>
  )
}

export default FilePreviewDialog
